import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, interval, merge, Observable, Subject } from 'rxjs';
import { LocalScanningWebApiService } from './local-scanning-web-api.service';
import { map, pluck, scan, shareReplay, switchMap, take } from 'rxjs/operators';
import { sortBy, without } from 'lodash-es';
import { AlertItem, Pending, PendingWithAlerts } from '../components/biographics/pending.model';
import { IBiographicPatch } from '../components/biographics/biographics.model';
import { filterEmptyValue } from '../rxjs-operators';

@Injectable({
  providedIn: 'root'
})
export class PendingCollectionService {
  api = inject(LocalScanningWebApiService);


  alerts$: Observable<PendingWithAlerts[]>;
  pending$: Observable<Pending[]>;
  allPending$: Observable<Pending[]>;

  alertCount$: Observable<number>;
  pendingCount$: Observable<number>;

  private add$ = new Subject<Pending>();
  private remove$ = new Subject<Pending>();
  private loadPending$ = new BehaviorSubject<Pending[]>([]);

  private pollingTriggerForScoringResults$ = new Subject<string>();
  private readonly POLLING_FOR_SCORING_RESULTS_INTERVAL = 10000;
  private readonly POLLING_FOR_SCORING_RESULTS_ITERATIONS = 6;

  constructor() {
    const loadPending$ = this.loadPending$.pipe(
      switchMap(() => {
        return this.api.getPending();
      }),
      shareReplay(1),
    );

    const pendingScan$ = merge(
      this.remove$.pipe(
        map((doc: Pending): PendingOperation => (docs: Pending[]) => without(docs, doc)),
      ),
      this.add$.pipe(
        map((doc: Pending): PendingOperation => (docs: Pending[]) => docs.concat(doc)),
      ),
      loadPending$.pipe(
        map((items: Pending[]): PendingOperation => () => items)
      )
    ).pipe(
      scan((alerts: Pending[], operation: PendingOperation) => operation(alerts), []),
      shareReplay(1),
    );

    this.allPending$ = pendingScan$;

    // A 'submittedAwaitingResponse' alert means we are waiting for the UDB scoring service.
    // These should not display to end users unless we get a real submit error in the callback.
    this.alerts$ = pendingScan$.pipe(
      filterEmptyValue(),
      map(pendings => pendings.filter(doc => doc.alerts
        && doc.alerts.length
        && doc.alerts[0].alertType !== 'submittedAwaitingResponse') as PendingWithAlerts[]),
    );

    this.pending$ = pendingScan$.pipe(
      filterEmptyValue(),
      map(pendings => pendings.filter(doc => !doc.alerts || !doc.alerts.length)),
    );

    this.alertCount$ = this.alerts$.pipe(
      pluck('length'),
    );

    this.pendingCount$ = this.pending$.pipe(
      pluck('length'),
    );

    // Observable for polling the database to get submit alerts after submitting to scoring.
    // The UDB service will call us back with any errors that occurred. Those errors become submit alerts,
    // which we want to display back to the user without them having to manually refresh their browser.
    this.pollingTriggerForScoringResults$.pipe(
      switchMap(() => interval(this.POLLING_FOR_SCORING_RESULTS_INTERVAL)),
      take(this.POLLING_FOR_SCORING_RESULTS_ITERATIONS),
    )
      .subscribe((iterationNum) => {
        console.log('Polling #' + iterationNum);
        this.syncPending();
      });
  }

  getPending(id: string): Observable<Pending> {
    return this.allPending$.pipe(
      map(pendings => pendings.find(pending => pending.lithocode === id)),
      filterEmptyValue(),
    );
  }

  getAlertItems(id: string): Observable<AlertItem[]> {
    return this.alerts$.pipe(
      map(pendings => pendings.find(pending => pending.lithocode === id)),
      filterEmptyValue(),
      pluck('alerts'),
      map(alerts => sortBy(alerts, (alert) => +alert.pageId)),
    );
  }

  clearAlertsForId(id: string): void {
    this.getPending(id)
      .pipe(take(1))
      .subscribe(pending => {
        this.clearAlertItems(pending);
      });
  }

  clearAlertItems(pending: Pending | PendingWithAlerts) {
    this.remove$.next(pending);
    const newPending = new Pending({
      ...pending,
      alerts: null,
    });
    this.add$.next(newPending);
  }

  clearAlertsForEach(pendings: Pending[]) {
    for (const pending of pendings) {
      this.clearAlertItems(pending);
    }
  }

  /* Update pending and clear bio alerts */
  updateWithBiographicPatch(pending: Pending, patch: IBiographicPatch) {
    this.remove(pending);
    const addPending = new Pending({
      ...pending,
      ...patch,
    });
    addPending.clearBiographicAlerts();
    this.add(addPending);
  }

  add(pending: Pending | PendingWithAlerts) {
    this.add$.next(pending);
  }

  remove(docs: Pending | Pending[] | PendingWithAlerts | PendingWithAlerts[]) {
    if (docs instanceof Pending) {
      docs = [docs];
    }
    for (const doc of docs) {
      this.remove$.next(doc);
    }
  }

  /* Resync all pending by calling the api again */
  syncPending() {
    this.loadPending$.next([]);
  }

  pollForNewAlerts() {
    this.pollingTriggerForScoringResults$.next('');
  }
}

type PendingOperation = (docs: Pending[]) => Pending[];

